/**
 * Session-token-aware fetch for an embedded Shopify app.
 *
 * App Bridge v4 exposes a global `shopify` object (loaded via the CDN script in
 * index.html). `shopify.idToken()` returns a short-lived session token signed
 * by Shopify. The backend's validateAuthenticatedSession middleware accepts it
 * as a Bearer token and performs token exchange on first hit, so a freshly
 * installed iframe immediately gets a valid session without a visible OAuth
 * redirect.
 *
 * Falling back to a plain fetch is intentional for local/standalone dev where
 * the admin host isn't injecting the App Bridge global.
 */
function getShopifyGlobal() {
    if (typeof window === 'undefined')
        return undefined;
    return window.shopify;
}
export async function getSessionToken() {
    const shopify = getShopifyGlobal();
    if (!shopify?.idToken)
        return null;
    try {
        return await shopify.idToken();
    }
    catch (err) {
        console.warn('[api] failed to get session token:', err);
        return null;
    }
}
/**
 * Shopify signals "auth expired / not authenticated" by returning these
 * headers on any authenticated endpoint. We honour them by redirecting the
 * top-level window (breaking out of the iframe) to the reauth URL.
 */
function handleReauth(res) {
    if (res.status !== 401 && res.status !== 403)
        return false;
    const reauth = res.headers.get('X-Shopify-API-Request-Failure-Reauthorize');
    const reauthUrl = res.headers.get('X-Shopify-API-Request-Failure-Reauthorize-Url');
    if (reauth !== '1' || !reauthUrl)
        return false;
    try {
        if (window.top) {
            window.top.location.href = reauthUrl;
        }
        else {
            window.location.href = reauthUrl;
        }
    }
    catch {
        window.location.href = reauthUrl;
    }
    return true;
}
export async function authFetch(input, init = {}) {
    const token = await getSessionToken();
    const headers = new Headers(init.headers);
    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    const res = await fetch(input, { ...init, headers, credentials: 'include' });
    if (handleReauth(res)) {
        throw new Error('Reauthorization required');
    }
    return res;
}
